import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import SearchInput from '../../components/SearchInput';
import RoleList from './RoleList';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { StaffDashboardStaffMember } from '../../api/models';

export interface StaffDataTypes {
	[key: string]: any[];
}

const Staff: React.FC = () => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get staff types from Redux store
	const staffTypes = useAppSelector((state) => state.lists.staffTypes);

	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Data to filter
	const [staffData, setStaffData] = React.useState<StaffDashboardStaffMember[]>([]);

	// Data used to search, it will not be changed and will always remain as its inital set value
	const [originalStaffData, setOriginalStaffData] = React.useState<StaffDashboardStaffMember[]>([]);

	// data to store current staff and past staff
	const [currentStaffData, setCurrentStaffData] = React.useState<StaffDataTypes>({});
	const [pastStaffData, setPastStaffData] = React.useState<StaffDataTypes>({});

	// data used for ordering the sections.
	const [defaultSectionOrder, setDefaultSectionOrder] = React.useState<(string | undefined)[]>([]);
	const [dynamicSectionOrder, setDynamicSectionOrder] = React.useState<(string | undefined)[]>([]);

	// Function to get Staff data from api and update state
	const getStaffData = async () => {
		setIsLoading(true);

		const response = await apibridge.getStaffData();

		response.result = response.result || [];

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setStaffData(response.result);

			setOriginalStaffData(response.result);
		}
	};

	// filter logic
	const filterData = () => {
		if (staffTypes && staffTypes.length) {
			const defaultDataOrder = staffTypes.map((item) => item.value);

			const dynamicDataOrder: string[] = [];
			let currentStaff: StaffDataTypes = {};
			let pastStaff: StaffDataTypes = {};

			if (staffData.length > 0) {
				staffData.forEach((staff) => {
					if (staff.staffType) {
						const role = staff.staffType;

						if (!defaultDataOrder.includes(role)) {
							defaultDataOrder.push(role);
						}

						if (!dynamicDataOrder.includes(role)) {
							dynamicDataOrder.push(role);
						}

						if (staff.isCurrentStaff) {
							if (currentStaff[role]) {
								currentStaff[role].push(staff);
							} else {
								currentStaff[role] = [];
								currentStaff[role].push(staff);
							}
						} else {
							if (pastStaff[role]) {
								pastStaff[role].push(staff);
							} else {
								pastStaff[role] = [];
								pastStaff[role].push(staff);
							}
						}
					}
				});

				setCurrentStaffData(currentStaff);
				setPastStaffData(pastStaff);

				setDefaultSectionOrder(defaultDataOrder);
				setDynamicSectionOrder(dynamicDataOrder);
			}
		}
	};

	// Calling data from api and set staff data from api to staffData state
	React.useEffect(() => {
		getStaffData();
	}, []);

	// filter data once we get the data from api.
	React.useEffect(() => {
		filterData();
	}, [staffData]);

	// State to control active tab and active content
	const [activeTab, setActiveTab] = React.useState<string>('tab1');

	return (
		<div className="page-general-layout staff-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<nav className="page-header-section staff-page mbtom-30">
									<div className="header-left">
										<Button
											className={`staff-category pdl-0 ${activeTab === 'tab1' ? 'active' : ''}`}
											onClick={() => setActiveTab('tab1')}
										>
											Current
										</Button>

										{isOrganisationAdmin && (
											<Button
												className={`staff-category ${activeTab === 'tab2' ? 'active' : ''}`}
												onClick={() => setActiveTab('tab2')}
											>
												Past
											</Button>
										)}
									</div>

									<div className="header-right">
										<SearchInput
											originalStaffData={originalStaffData}
											setStaffData={setStaffData}
											searchPlaceholder="Search"
										/>
									</div>
								</nav>

								<section className="staff-content">
									{activeTab === 'tab1' && (
										<div className="mbtom-30">
											{defaultSectionOrder.map((role, index) => {
												if (role && dynamicSectionOrder.includes(role)) {
													const peopleList: StaffDashboardStaffMember[] = currentStaffData[role] || [];
													return <RoleList key={index} role={role} peopleList={peopleList} isPastStaff={false} />;
												}
											})}
										</div>
									)}

									{activeTab === 'tab2' && (
										<div className="mbtom-30">
											{defaultSectionOrder.map((role, index) => {
												if (role && dynamicSectionOrder.includes(role)) {
													const peopleList: StaffDashboardStaffMember[] = pastStaffData[role] || [];

													return <RoleList key={index} role={role} peopleList={peopleList} isPastStaff={true} />;
												}
											})}
										</div>
									)}

									<ScrollToTopButton />
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default Staff;
